import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  activeInactiveHsnSacCode,
  deleteHsnSacCode,
  getAllHsnSacCode,
  getHsnSacCodeFields,
  HSN_SAC_CODE_ADVANCE_SEARCH_INITIAL_VALUE,
  IHsnSacCode,
  IHsnSacCodeAdvancedSearch,
} from "./HSNCodeViewController";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IPagination } from "../../data/AppInterface";
import { AUDIT_LOG_TYPE, BIT_VALUE } from "../../utils/AppEnumerations";
import {
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import {
  deleteIcon,
  editIcon,
  eyeIcon,
  timeLineIcon,
} from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { ORDER_BY } from "../../utils/AppConstants";
import HsnSacCodeForm from "./HSNCodeForm";
import AuditLog from "../audit-log/AuditLogView";

let timer: ReturnType<typeof setTimeout>;

const HSNCodeView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [readOnly, setReadOnly] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [hsnSacCodeList, setHsnSacCodeList] = useState<IHsnSacCode[]>([]);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [advanceSearch, setAdvanceSearch] = useState<IHsnSacCodeAdvancedSearch>(
    HSN_SAC_CODE_ADVANCE_SEARCH_INITIAL_VALUE,
  );
  const [hsnSacCodeToView, setHsnSacCodeToView] = useState<IHsnSacCode>();

  useEffect(() => {
    fetchHsnSacCode({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  const fetchHsnSacCode = (paginationPlayLoad: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await getAllHsnSacCode(
        setHsnSacCodeList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const handleHsnSacCodeStatus = async (values: IHsnSacCode) => {
    const isStatusChange =
      values.is_deleted === BIT_VALUE.Zero &&
      (await activeInactiveHsnSacCode(
        values.id,
        values.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One,
      ));
    if (isStatusChange) {
      setHsnSacCodeList(toggleStatus(hsnSacCodeList, values.id));
    }
  };

  const customActionView = (item: IHsnSacCode) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setHsnSacCodeToView(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>
        {!item.end_date && (
          <div
            className="ml-2 size-5 cursor-pointer"
            title={t("edit_tooltip_icon")}
            onClick={() => {
              setShowForm(true);
              setHsnSacCodeToView(item);
            }}
          >
            {editIcon}
          </div>
        )}
        {item.is_deleted === BIT_VALUE.Zero && (
          <div
            className="ml-2 size-5 cursor-pointer"
            title={t("delete_tooltip_icon")}
            onClick={() => {
              setShowDeleteConfirmation(true);
              setHsnSacCodeToView(item);
            }}
          >
            {deleteIcon}
          </div>
        )}
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("audit_log_tooltip_icon")}
          onClick={() => {
            setHsnSacCodeToView(item);
            setReadOnly(true);
            setShowAuditLog(true);
          }}
        >
          {timeLineIcon}
        </div>
      </div>
    );
  };

  const handleDelete = async (value: boolean) => {
    if (value && hsnSacCodeToView) {
      const data = await deleteHsnSacCode(hsnSacCodeToView.id);
      if (data) {
        fetchHsnSacCode(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setHsnSacCodeToView(undefined);
  };

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setShowAuditLog(false);
    setReadOnly(false);
    if (hsnSacCodeToView) {
      setHsnSacCodeToView(undefined);
    }
    if (status) {
      fetchHsnSacCode(pagination);
    }
  };
  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        searchText={enterpriseSearch}
        handleDeleteCheckBox={(ans) => {
          setAdvanceSearch((prev) => ({
            ...prev,
            sd: ans ? BIT_VALUE.One : BIT_VALUE.Zero,
          }));
        }}
        filterCheckBox={advanceSearch.sd === BIT_VALUE.One}
        refreshTable={() => {
          fetchHsnSacCode(pagination);
        }}
        title={t("hsn_sac_code")}
      />

      <ICTable<IHsnSacCode>
        isLoading={isLoading}
        totalFlex="grid grid-cols-13"
        fields={getHsnSacCodeFields(
          customActionView,
          handleHsnSacCodeStatus,
          t,
        )}
        data={hsnSacCodeList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchHsnSacCode({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchHsnSacCode({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchHsnSacCode({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && (
        <HsnSacCodeForm
          handleClose={handleCloseForm}
          hsnSacCodeToEdit={hsnSacCodeToView}
          readOnly={readOnly}
        />
      )}
      {showAuditLog && hsnSacCodeToView && (
        <AuditLog
          handleClose={handleCloseForm}
          auditLogRecordId={hsnSacCodeToView.id}
          auditLogType={AUDIT_LOG_TYPE.HsnCode}
          titleName={t("hsn_code")}
        />
      )}
      {showDeleteConfirmation && hsnSacCodeToView && (
        <ICConfirmationModal
          title={t("delete_hsn_sac_code")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", hsnSacCodeToView.hsn_sac_code],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};
export default HSNCodeView;
